import {
  Box,
  FormControl,
  FormHelperText,
  TextField,
  styled,
  useTheme,
  Autocomplete,
} from "@mui/material";
import { useField } from "formik";
import PTDynamicFormField from "./PTDynamicFormField";
import { useLanguage } from "../../hooks";
import { translate } from "../../utils/translate";
import { is } from "date-fns/locale";
import PTTranslate from "./PTTranslate";
import i18next from 'i18next';

const getTranslationString = (element) => {
  return i18next.t(element.props.tk);
};

const StyledAutocomplete = styled(Autocomplete)(({ theme, height, disabled }) => ({
  "& .MuiAutocomplete-inputRoot": {
    height: `${height}px`,
    padding: '0 8px',
    backgroundColor: disabled ? "#e0e0e0" : "#FFFFFF",
  },
  "& .MuiOutlinedInput-root": {
    padding: '0 !important',
  },
}));

const CustomBox = styled(Box)`
  overflow-x: auto;
  max-width: 100%;
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;
  margin-top: 2px;
`;

export const PTAutocompleteInput = ({ ...props }) => {
  return (
    <PTDynamicFormField {...props}>
      <PTAutocompleteInputUI {...props} />
    </PTDynamicFormField>
  );
};

export const PTAutocompleteInputUI = ({
  name,
  label,
  options,
  required,
  error,
  height = 40,
  formRef,
  innerValues = [],
  placeHolder,
  valueRenderer = (item) => item?.value,
  onChange = () => {},
  allowText = false,
  textName,
  isDisabled = (inputName)=> false,
  ...restProps
}) => {
  const disabled = isDisabled(name)
  const theme = useTheme();
  const { currentLang } = useLanguage();
  const [field, meta, helpers] = useField(name);
  if (!options) throw new Error("options prop is required for AutocompleteInput");
  const isRTL = currentLang === "ar";
  const textAlignment = isRTL ? "right" : "left";
  const directionStyle = isRTL ? { direction: "rtl" } : {};
  const borderStyles = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        border: error ? "1px solid red" : "1px solid gray",
      },
      "&:hover fieldset": {
        border: error ? "1px solid red" : "1px solid gray",
      },
      "&.Mui-focused fieldset": {
        border: error ? "1px solid red" : "1px solid gray",
      },
    },
    "& .MuiSvgIcon-root": {
    color: theme.palette.primary.main,
    ...(isRTL && { left: 7, right: "auto" }),
  },
  };
  
  return (
    <FormControl fullWidth error={meta.touched && !!meta.error} sx={directionStyle}>
      <StyledAutocomplete
        {...field}
        freeSolo={allowText}
        options={options}
        disabled= {disabled}
        value={options.find(option => {
          if (typeof field.value === 'object' && field.value !== null) {
            return option.id === field.value.id;
          }
          return valueRenderer(option) === field.value;
        }) || null}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => {
          if (allowText && typeof value === 'string') {
            return option?.value === value;
          }
          return valueRenderer(option) === valueRenderer(value);
        }}
        componentsProps={{
          paper: {
            sx: {
              direction: isRTL ? "rtl" : "ltr",
              "& .MuiAutocomplete-option": {
                justifyContent: isRTL ? "flex-end" : "flex-start",
                textAlign: textAlignment,
                fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "inherit"
              }
            }
          }
        }}
        onChange={(event, value, reason) => {
          if (allowText && (typeof value === 'string' || !value)) {
            formRef?.current?.setFieldValue(textName, value);
            helpers.setValue(value || '');
          } else {
            helpers.setValue(value ? valueRenderer(value) : '');
            onChange(event, value, reason);
            innerValues.forEach((item) => {
              const capitalizedItem = item.charAt(0).toUpperCase() + item.slice(1);
              formRef?.current?.setFieldValue(`${name}${capitalizedItem}`, valueRenderer(value)?.[item]);
            });
          }
        }}
        sx={borderStyles}
        renderInput={(params) => (
          <TextField
            {...params}
            error={!!error}
            placeholder={getTranslationString(<PTTranslate tk={placeHolder} />)}
            sx={{
              backgroundColor: disabled ? "#e0e0e0" : "#FFFFFF",
              '& .MuiInputBase-root': {
                height: `${height}px`,
                textAlign: textAlignment,
                justifyContent: isRTL ? 'flex-end' : 'flex-start',
                // Adjust padding for RTL to prevent icon overlap
                paddingLeft: isRTL ? '32px' : '8px',
                paddingRight: isRTL ? '8px' : '32px',
              },
              '& .MuiInputBase-input': {
                textAlign: textAlignment,
                fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "inherit",
              },
              '& .MuiAutocomplete-endAdornment': {
                ...isRTL && { position: "relative", padding: "5px", marginLeft: "5px" },
              },
              // Adjust clear icon position for RTL
              '& .MuiAutocomplete-clearIndicator': {
                marginLeft: isRTL ? '4px' : 'unset',
                marginRight: isRTL ? 'unset' : '4px',
              }
            }}
          />
        )}
        {...restProps}
      />
      <FormHelperText sx={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "inherit" }}>
        {meta.touched && translate(meta.error)}
      </FormHelperText>
    </FormControl>
  );
};

export default PTAutocompleteInput;